import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Button,
  Input,
  Table,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getEmployeeLeaves } from "../../components/Api/GmsEmployeeLeave";
import { getDailyAttendance, saveDailyAttendance } from "../../components/Api/GmsDailyAttendance";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };
  
  let businessId, branchId;
  
  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }
  
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const DailyAttendancePage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [attendanceData, setAttendanceData] = useState({}); // { employeeId: { status, isLeave, isDisabled } }
  const [employeeLeaves, setEmployeeLeaves] = useState([]);
  const [existingAttendance, setExistingAttendance] = useState([]);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsDailyAttendance');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsDailyAttendance');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsDailyAttendance');
  const isAdmin = isUserAdmin();
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business && !selectedBusiness) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && selectedBusiness) {
      const branch = branches.find(b => b.value === session.branchId);
      if (branch && !selectedBranch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId, selectedBusiness]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  // Load employees when filters change
  useEffect(() => {
    if (selectedBusiness && selectedDate) {
      loadEmployees();
    }
  }, [selectedBusiness, selectedBranch, selectedDate]);

  // Load existing attendance first, then process leaves
  useEffect(() => {
    if (selectedBusiness && selectedDate && employees.length > 0) {
      const loadData = async () => {
        await loadExistingAttendance();
        await loadEmployeeLeaves();
      };
      loadData();
    }
  }, [selectedBusiness, selectedBranch, selectedDate, employees.length]);

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({});
      const data = Array.isArray(response) ? response : (response.data || []);
      const businessOptions = data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_NameAr
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  };

  const loadBranches = async (businessId) => {
    try {
      const response = await getBranches({ Business_Id: businessId });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      const branchOptions = branchData.map(b => ({
        value: b.branch_Id,
        label: b.branch_NameEn || b.branch_NameAr
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadEmployees = async () => {
    if (!selectedBusiness) return;

    setLoading(true);
    try {
      const filter = {
        employee_Business_Id: selectedBusiness.value,
        OnWork: true
      };

      if (selectedBranch) {
        filter.employee_Branch_Id = selectedBranch.value;
      }

      const response = await getEmployees(filter);
      const employeeData = Array.isArray(response) ? response : (response.data || []);
      setEmployees(employeeData);
    } catch (error) {
      console.error("Error loading employees:", error);
      Swal.fire("Error", "Failed to load employees", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeLeaves = async () => {
    if (!selectedDate) return;

    try {
      const response = await getEmployeeLeaves({});
      const leavesData = Array.isArray(response) ? response : (response.data || []);
      setEmployeeLeaves(leavesData);
      
      // Process leaves and mark attendance
      processLeavesForDate(leavesData, selectedDate);
    } catch (error) {
      console.error("Error loading employee leaves:", error);
    }
  };

  const loadExistingAttendance = async () => {
    if (!selectedDate) return;

    try {
      // Format date as YYYY-MM-DD for API
      const dateObj = new Date(selectedDate);
      const formattedDate = dateObj.toISOString().split('T')[0];

      const filter = {
        Date: formattedDate
      };

      const response = await getDailyAttendance(filter);
      const attendanceRecords = Array.isArray(response) ? response : (response.data || []);
      setExistingAttendance(attendanceRecords);

      // Update attendance data with existing records
      const attendanceMap = {};
      attendanceRecords.forEach(record => {
        const status = record.status;
        // If status is 0 (On Leave) or 0.5 (Half Day Leave), mark as leave and disable
        const isLeaveStatus = status === 0 || status === 0.5;
        attendanceMap[record.employeeID] = {
          status: status,
          isLeave: isLeaveStatus,
          isDisabled: isLeaveStatus // Disable if it's a leave status
        };
      });
      
      // Merge with existing attendance data
      setAttendanceData(prev => {
        const merged = { ...prev };
        Object.keys(attendanceMap).forEach(empId => {
          merged[empId] = attendanceMap[empId];
        });
        return merged;
      });
    } catch (error) {
      console.error("Error loading existing attendance:", error);
    }
  };

  const processLeavesForDate = (leaves, date) => {
    const currentDate = new Date(date);
    currentDate.setHours(0, 0, 0, 0);

    setAttendanceData(prev => {
      const updatedAttendance = { ...prev };

      leaves.forEach(leave => {
        if (leave.is_Cancelled) return;

        const fromDate = new Date(leave.leave_FromDate);
        fromDate.setHours(0, 0, 0, 0);
        const toDate = new Date(leave.leave_ToDate);
        toDate.setHours(0, 0, 0, 0);
        const rejoinDate = leave.leave_RejoinDate ? new Date(leave.leave_RejoinDate) : null;
        if (rejoinDate) rejoinDate.setHours(0, 0, 0, 0);

        const employeeId = leave.leave_Employee_Id;

        // Check if current date falls within leave period
        let isOnLeave = false;
        if (currentDate >= fromDate && currentDate <= toDate) {
          isOnLeave = true;
        } else if (!rejoinDate && currentDate > toDate) {
          // If rejoin date is not set and current date is after leave period, still on leave
          isOnLeave = true;
        }

        if (isOnLeave) {
          const isHalfDay = leave.is_HalfDay || false;
          const status = isHalfDay ? 0.5 : 0; // Half Day = 0.5, Full Day Leave = 0

          const existing = updatedAttendance[employeeId];
          
          // If existing attendance shows leave status (0 or 0.5), don't change it
          if (existing && (existing.status === 0 || existing.status === 0.5)) {
            // Keep existing leave status but ensure it's disabled
            updatedAttendance[employeeId] = {
              ...existing,
              isLeave: true,
              isDisabled: true
            };
          } else {
            // Mark as leave and disable
            updatedAttendance[employeeId] = {
              status: status,
              isLeave: true,
              isDisabled: true // Disable other options for leave
            };
          }
        }
      });

      return updatedAttendance;
    });
  };

  const handleBusinessChange = (selectedOption) => {
    setSelectedBusiness(selectedOption);
    setSelectedBranch(null);
    setEmployees([]);
    setAttendanceData({});
  };

  const handleBranchChange = (selectedOption) => {
    setSelectedBranch(selectedOption);
    setAttendanceData({});
  };

  const handleDateChange = (e) => {
    const selectedDateValue = e.target.value;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDateValue);
    selected.setHours(0, 0, 0, 0);

    if (selected > today) {
      Swal.fire("Error", "Future dates are not allowed", "error");
      return;
    }

    setSelectedDate(selectedDateValue);
    setAttendanceData({});
  };

  const handleStatusChange = (employeeId, status) => {
    if (isViewOnly || !hasEditOrHigher) {
      Swal.fire("Error", "You don't have permission to edit attendance", "error");
      return;
    }

    const employeeData = attendanceData[employeeId];
    
    // Don't allow change if employee is on leave
    if (employeeData && employeeData.isLeave && employeeData.isDisabled) {
      Swal.fire("Warning", "Cannot change attendance status for employees on leave", "warning");
      return;
    }

    setAttendanceData(prev => ({
      ...prev,
      [employeeId]: {
        status: status,
        isLeave: false,
        isDisabled: false
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedBusiness || !selectedDate) {
      Swal.fire("Error", "Please select business and date", "error");
      return;
    }

    if (isViewOnly || !hasEditOrHigher) {
      Swal.fire("Error", "You don't have permission to save attendance", "error");
      return;
    }

    setSaving(true);
    try {
      const session = getUserSession();
      const attendanceRecords = [];

      // Process all employees - if no status set, default to 1 (Present/Full Day)
      employees.forEach(employee => {
        const employeeId = employee.employee_Id;
        const status = attendanceData[employeeId]?.status !== undefined 
          ? attendanceData[employeeId].status 
          : 1; // Default to Present (Full Day = 1)

        attendanceRecords.push({
          Date: selectedDate,
          EmployeeID: employeeId,
          Status: status,
          UpdatedBy: session.userId
        });
      });

      // Save all attendance records
      const savePromises = attendanceRecords.map(record => {
        // Format date properly
        const dateObj = new Date(record.Date);
        const formattedDate = dateObj.toISOString().split('T')[0];
        return saveDailyAttendance({
          ...record,
          Date: formattedDate
        });
      });
      const results = await Promise.all(savePromises);

      // Check for failures - API returns {id, message} on success
      // Successful response: {id: number, message: string} where id > 0
      // Failed response (from catch): {success: false, message: string}
      const failed = results.filter(r => {
        // Check if response exists
        if (!r) return true;
        
        // If response has success: false, it's a failure (from error catch)
        if (r.success === false) return true;
        
        // Check if id exists and is valid (id > 0 means success)
        // The API returns {id, message} on success, where id > 0
        if (r.id && r.id > 0) return false; // This is a success
        
        // If we get here, it's a failure
        return true;
      });
      
      if (failed.length > 0) {
        Swal.fire("Warning", `Failed to save ${failed.length} out of ${results.length} attendance records`, "warning");
      } else {
        Swal.fire("Success", `Attendance saved successfully for ${results.length} employee(s)`, "success");
        await loadExistingAttendance();
      }
    } catch (error) {
      console.error("Error saving attendance:", error);
      Swal.fire("Error", "Failed to save attendance", "error");
    } finally {
      setSaving(false);
    }
  };

  const getMaxDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Present';
      case 0.5: return 'Half Day';
      case -1: return 'Absent';
      case 0: return 'On Leave';
      case -2: return 'Off Day';
      default: return 'Present';
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title mb-0">Daily Attendance</h4>
            </div>

            <Form>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      max={getMaxDate()}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Company</Label>
                    <Select
                      value={selectedBusiness}
                      onChange={handleBusinessChange}
                      options={businesses}
                      isClearable
                      placeholder="Select Company"
                      maxMenuHeight={80}
                      styles={selectStyles}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Select
                      value={selectedBranch}
                      onChange={handleBranchChange}
                      options={branches}
                      isClearable
                      isDisabled={!selectedBusiness}
                      placeholder="Select Branch"
                      styles={selectStyles}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>&nbsp;</Label>
                    <div>
                      {hasEditOrHigher && (
                        <Button
                          color="primary"
                          onClick={handleSave}
                          disabled={saving || !selectedBusiness || !selectedDate}
                        >
                          {saving ? <ClipLoader size={20} color="#fff" /> : 'Save Attendance'}
                        </Button>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </Form>

            {loading ? (
              <div className="text-center mt-4">
                <ClipLoader color="#123abc" loading={loading} size={50} />
              </div>
            ) : (
              <>
                {selectedBusiness && employees.length > 0 ? (
                  <div className="mt-4">
                    <Table responsive striped bordered>
                      <thead>
                        <tr>
                          <th>Employee Code</th>
                          <th>Employee Name</th>
                          <th>Absent</th>
                          <th>On Leave</th>
                          <th>Off Day</th>
                          <th>Half Day</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map((employee) => {
                          const employeeId = employee.employee_Id;
                          const empAttendance = attendanceData[employeeId] || {};
                          const status = empAttendance.status !== undefined ? empAttendance.status : 1;
                          const isDisabled = empAttendance.isDisabled || false;
                          const isLeave = empAttendance.isLeave || false;

                          return (
                            <tr key={employeeId}>
                              <td>{employee.employee_Code || '-'}</td>
                              <td>{employee.employee_NameEn || employee.employee_NameAr || '-'}</td>
                              <td className="text-center">
                                <Input
                                  type="radio"
                                  name={`attendance_${employeeId}`}
                                  checked={status === -1}
                                  onChange={() => handleStatusChange(employeeId, -1)}
                                  disabled={isDisabled || isViewOnly || !hasEditOrHigher}
                                />
                              </td>
                              <td className="text-center">
                                <Input
                                  type="radio"
                                  name={`attendance_${employeeId}`}
                                  checked={status === 0}
                                  onChange={() => handleStatusChange(employeeId, 0)}
                                  disabled={isDisabled || isViewOnly || !hasEditOrHigher}
                                />
                              </td>
                              <td className="text-center">
                                <Input
                                  type="radio"
                                  name={`attendance_${employeeId}`}
                                  checked={status === -2}
                                  onChange={() => handleStatusChange(employeeId, -2)}
                                  disabled={isDisabled || isViewOnly || !hasEditOrHigher}
                                />
                              </td>
                              <td className="text-center">
                                <Input
                                  type="radio"
                                  name={`attendance_${employeeId}`}
                                  checked={status === 0.5}
                                  onChange={() => handleStatusChange(employeeId, 0.5)}
                                  disabled={isDisabled || isViewOnly || !hasEditOrHigher}
                                />
                              </td>
                              <td>
                                <span className={`badge ${
                                  status === 1 ? 'badge-success' :
                                  status === 0.5 ? 'badge-warning' :
                                  status === -1 ? 'badge-danger' :
                                  status === 0 ? 'badge-info' :
                                  'badge-secondary'
                                }`}
                                style={{
                                  backgroundColor: status === 1 ? '#28a745' : // Green for Present
                                           status === 0.5 ? '#ffc107' : // Yellow/Orange for Half Day
                                           status === -1 ? '#dc3545' : // Red for Absent
                                           status === 0 ? '#17a2b8' : // Blue for On Leave
                                           '#6c757d', // Gray for Off Day
                                  color: '#ffffff',
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  {getStatusLabel(status)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    <div className="mt-3">
                      <small className="text-muted">
                        <strong>Status Values:</strong> Full day (Present) = 1, Half Day = 0.5, Absent = -1, On Leave = 0, Off Day = -2
                        <br />
                        If no radio button is selected, the employee is marked as Present (Full Day = 1)
                      </small>
                    </div>
                  </div>
                ) : selectedBusiness ? (
                  <div className="text-center mt-4">
                    <p>No employees found for the selected filters.</p>
                  </div>
                ) : (
                  <div className="text-center mt-4">
                    <p>Please select a company to view employees.</p>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default DailyAttendancePage;
